import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';

import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';
import { IReqPickups, IResPickupsOK } from '@/types/parcel/orders/pickups.interface';

import useScheduleModal from './useScheduleModal';

interface Props {
  selected: string[];
  isOpen: boolean;
  onClose: () => void;
  fetchShipments: ({ search }: IReqPickups) => Promise<void>;
  orders: IResPickupsOK['data']['ordersData'] | [];
}

function ScheduleModal(props: Props) {
  const hook = useScheduleModal({
    orders: props.orders,
    selected: props.selected,
    fetchShipments: props.fetchShipments,
    onClose: props.onClose,
  });
  const onCloseClean = () => {
    props.onClose();
    hook.cleanReschedule();
  };

  const allProps = { ...props, ...hook, onCloseClean };

  return (
    <>
      {hook.successModal ? (
        <Confirm {...allProps} selected={hook.selectedTemp} />
      ) : (
        <Schedule {...allProps} />
      )}
    </>
  );
}

export default ScheduleModal;

interface ScheduleProps extends Props {
  hasNAL: boolean;
  allAreNAL: boolean;
  rescheduleDate: Date | null;
  availableToday: boolean;
  selected: string[];
  onChgReschedule: (date: Date | null) => void;
  confirmAndReschedule: () => void;
  closeSuccessModal: () => void;
  successModal: boolean;
  onCloseClean: () => void;
}

const Schedule = ({
  selected,
  onChgReschedule,
  isOpen,
  rescheduleDate,
  availableToday,
  confirmAndReschedule,
  onCloseClean,
}: ScheduleProps) => {
  const theme = useTheme();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseClean}
      title={<>Programar {selected.length > 1 ? 'recolecciones' : 'recolección'}</>}
      maxWidth="sm"
      actions={
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 1.5, width: '100%' }}>
          <Button onClick={confirmAndReschedule} disabled={!rescheduleDate} sx={{ minWidth: 200 }}>
            Programar
          </Button>
        </Box>
      }
    >
      <Box sx={{ padding: (theme) => theme.spacing(0, 2, 2, 2) }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
          <CalendarMonthIcon
            sx={{
              fontSize: 50,
              color: theme.palette.primary.main,
            }}
          />
        </Box>

        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            color: theme.palette.grey[700],
            mb: 2,
            fontWeight: 500,
          }}
        >
          Selecciona la fecha en que pasaremos a recolectar{' '}
          {selected.length > 1 ? 'tus órdenes' : 'tu orden'}
        </Typography>

        <Box
          sx={{
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
            padding: 1.5,
            mb: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.grey[600],
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              display: 'block',
              mb: 1,
            }}
          >
            {selected.length > 1 ? 'Números de seguimiento' : 'Número de seguimiento'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selected.map((trackingId) => (
              <Box
                key={trackingId}
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                  padding: '4px 12px',
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {trackingId}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DatePicker
              label={'Fecha de recolección'}
              sx={{ width: '100%' }}
              shouldDisableDate={(date) => {
                return !availableToday && isSameDay(date, new Date());
              }}
              disablePast
              format="dd/MM/yyyy"
              value={rescheduleDate ? new Date(rescheduleDate) : null}
              onChange={(date) => {
                if (date) {
                  onChgReschedule(date);
                } else {
                  onChgReschedule(null);
                }
              }}
              slotProps={{
                popper: {
                  placement: 'right-start',
                  disablePortal: false,
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 0],
                      },
                    },
                    {
                      name: 'preventOverflow',
                      enabled: true,
                      options: {
                        altBoundary: true,
                        tether: false,
                        padding: 8,
                      },
                    },
                    {
                      name: 'flip',
                      enabled: true,
                      options: {
                        fallbackPlacements: ['left-start', 'bottom-start'],
                      },
                    },
                  ],
                  sx: {
                    zIndex: 1400,
                  },
                },
                desktopPaper: {
                  sx: {
                    boxShadow: 3,
                    maxHeight: '400px',
                    overflow: 'auto',
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Box>
    </Modal>
  );
};

const Confirm = (props: ScheduleProps) => {
  const theme = useTheme();

  return (
    <Modal
      isOpen={props.successModal}
      onClose={props.closeSuccessModal}
      title="¡Recolección programada!"
      maxWidth="sm"
    >
      <Box sx={{ padding: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {props.rescheduleDate && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FmdGoodIcon
                sx={{
                  fontSize: 24,
                  color: theme.palette.grey[500],
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.grey[800],
                  fontSize: '1rem',
                }}
              >
                Pasaremos por tu{' '}
                <strong style={{ color: theme.palette.primary.main }}>
                  dirección de recolección
                </strong>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CalendarMonthIcon
                sx={{
                  fontSize: 24,
                  color: theme.palette.grey[500],
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.grey[800],
                  fontSize: '1rem',
                }}
              >
                El día{' '}
                <strong style={{ color: theme.palette.primary.main }}>
                  {format(props.rescheduleDate, 'dd/MM/yyyy')}
                </strong>
              </Typography>
            </Box>
          </>
        )}

        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
          <Button onClick={props.closeSuccessModal} sx={{ minWidth: 200 }}>
            Cerrar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
